import { BadgeProps, Button as MuiButton } from "@mui/material";
import Badge from "@mui/material/Badge";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { SxProps } from "@mui/system";
import { Theme } from "klayowebshared";
import React, { Component, ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { Tooltip } from "./Tooltip";

interface ButtonProps {
  label: ReactNode;
  size: "sm" | "md" | "lg";
  theme: typeof Theme;
  variant: "outlined" | "filled" | "simple" | "text";
  path: string;
  href: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>, value?: string) => void;
  disabled: boolean;
  autoFocus: boolean;
  icon: React.ReactNode;
  startIcon: React.ReactNode;
  endIcon: React.ReactNode;
  sx: SxProps;
  className: string;
  badgeProps: BadgeProps & {
    show?: boolean;
  };
  color: string;
  hoverColor: string;
  activeColor: string;
  padding: string;
  labelColor: string;
  target: string;
  filledLabelColor: string;
  blurDetect: number;
  type: "button" | "submit" | "reset";
  form: string;
  isLoading: boolean;
  showLabelFromBreakpoint: string;
  tooltip: React.ReactNode;
  onMouseDown: (event: React.MouseEvent<HTMLButtonElement>) => void;
  htmlFor?: string;
  edge?: "start" | "end";
  /** The accessible name when the visible label isn't enough (e.g. several "Return" buttons on one page). */
  ariaLabel?: string;
}

export class Button extends Component<Partial<ButtonProps>> {
  color = Theme.getStyleVar("--white-wh-900");
  hoverColor = Theme.getStyleVar("--neon-blue-nb-050");
  activeColor = Theme.getStyleVar("--neon-blue-nb-100");
  filledColor = Theme.getStyleVar("--neon-blue-nb-500");
  filledHoverColor = Theme.getStyleVar("--neon-blue-nb-700");
  filledActiveColor = Theme.getStyleVar("--neon-blue-nb-900");
  iconColor = Theme.getStyleVar("--neon-blue-nb-500");
  filledIconColor = Theme.getStyleVar("--white-wh-900");

  static padding: Record<string, string> = { sm: "16px 12px", md: "10px 16px", lg: "12px 24px" };
  static iconPadding: Record<string, string> = { sm: "8px", md: "8px", lg: "12px" };
  static iconSize: Record<string, string> = { sm: "20px", md: "20px", lg: "24px" };
  static fontSize: Record<string, string> = { sm: "14px", md: "14px", lg: "16px" };
  static size: Record<string, string> = { sm: "32x", md: "40px", lg: "40px" };
  static focusBorderRadius: Record<string, string> = { sm: "20px", md: "24px", lg: "28px" };
  static focusOffset = "-4px";
  static outlinedfocusOffset = "-5px";

  static defaultProps = {
    size: "md",
    variant: "outlined",
    filledLabelColor: "var(--white-wh-900)",
    target: null
  };

  buttonRef: React.RefObject<HTMLButtonElement | null>;

  constructor(props: Partial<ButtonProps>) {
    super(props);

    this.buttonRef = React.createRef();
  }

  componentDidUpdate(prevProps: any) {
    // Check to clear focus of button
    if (prevProps.blurDetect !== this.props.blurDetect) {
      // Clear the focus using the ref
      this.buttonRef.current?.blur();
    }
  }

  renderButton() {
    const {
      ariaLabel,
      label,
      size,
      theme,
      variant,
      path,
      href,
      onClick,
      disabled,
      autoFocus,
      icon,
      startIcon,
      endIcon,
      sx,
      className,
      badgeProps,
      color,
      hoverColor,
      activeColor,
      padding,
      labelColor,
      target,
      filledLabelColor,
      showLabelFromBreakpoint,
      isLoading,
      type,
      form,
      onMouseDown
    } = this.props;

    const btnClass =
      "klayo-button" +
      (icon ? " klayo-iconbutton" : "") +
      (className ? " " + className : "") +
      (size ? " klayo-button-" + size : "") +
      (variant && !icon ? " klayo-button-" + variant : "");

    const isOutlined = variant === "outlined";
    const isFilled = variant === "filled";

    // Default label color per variant (text is a blue link-style action); an explicit
    // labelColor prop always wins. Uses CSS var() so the value resolves at paint time —
    // avoids the dev/prod discrepancy from reading CSS vars via getComputedStyle at module load.
    const resolvedLabelColor =
      labelColor ?? (variant === "text" ? "var(--neon-blue-nb-500)" : "var(--grey-gr-900)");

    const style: Record<string, any> = {
      padding: icon
        ? Button.iconPadding[size || Button.defaultProps.size]
        : Button.padding[size || Button.defaultProps.size],
      backgroundColor: color || (isFilled ? this.filledColor : icon ? null : this.color),
      "&:hover": {
        backgroundColor: hoverColor || (isFilled ? this.filledHoverColor : this.hoverColor)
      },
      "&:active": {
        backgroundColor: activeColor || (isFilled ? this.filledActiveColor : this.activeColor)
      },
      "&:focus::before": {
        border: "2px solid var(--neon-blue-nb-400)",
        borderRadius: Button.focusBorderRadius[size || Button.defaultProps.size],
        top: isOutlined ? Button.outlinedfocusOffset : Button.focusOffset,
        right: isOutlined ? Button.outlinedfocusOffset : Button.focusOffset,
        bottom: isOutlined ? Button.outlinedfocusOffset : Button.focusOffset,
        left: isOutlined ? Button.outlinedfocusOffset : Button.focusOffset
      },
      ["& .MuiButton-startIcon, & .MuiButton-endIcon"]: {
        width: Button.iconSize[size || Button.defaultProps.size]
      },
      ["& .klayo-button_label"]: {
        color: isFilled ? filledLabelColor : resolvedLabelColor,
        fontSize: Button.fontSize[size || Button.defaultProps.size],
        fontWeight: isFilled ? "400" : "600"
      },
      ["&.Mui-disabled .klayo-button_label"]: {
        color: "inherit"
      },
      ['& .MuiButton-startIcon svg path[fill="#111827"], & .MuiButton-startIcon svg path[fill="#727DF9"], & .MuiButton-endIcon svg path[fill="#111827"], & .MuiButton-endIcon svg path[fill="#727DF9"], & .MuiButton-endIcon svg path[fill="#1B3DF2"], & .MuiButton-endIcon svg path[fill="#9CA3AF"]']:
        {
          fill: isFilled ? this.filledIconColor : this.iconColor
        },
      ...sx
    };

    if (theme && showLabelFromBreakpoint) {
      style[theme.breakpoints.down(showLabelFromBreakpoint)] = {
        padding: "0!important",
        minWidth: Button.size[size || Button.defaultProps.size]
      };

      style["& .klayo-button_label"] = {
        [theme.breakpoints.down(showLabelFromBreakpoint)]: {
          display: "none"
        },
        [theme.breakpoints.up(showLabelFromBreakpoint)]: {
          display: "block"
        }
      };

      style["& .MuiButton-startIcon, & .MuiButton-endIcon"][
        theme.breakpoints.down(showLabelFromBreakpoint)
      ] = {
        margin: "0"
      };
    }

    return icon ? (
      <IconButton
        ref={this.buttonRef}
        className={btnClass}
        onClick={onClick}
        disabled={disabled}
        disableRipple={true}
        aria-label={ariaLabel}
        {...(path && {
          to: path,
          component: NavLink
        })}
        sx={style}
      >
        {badgeProps && badgeProps.show !== false ? <Badge {...badgeProps}>{icon}</Badge> : icon}
      </IconButton>
    ) : (
      <MuiButton
        ref={this.buttonRef}
        className={btnClass}
        {...(path
          ? {
              href: href,
              target: target,
              to: path,
              component: NavLink
            }
          : href
            ? {
                href: href,
                target: target,
                component: "a"
              }
            : {})}
        startIcon={
          isLoading ? (
            <CircularProgress
              size={20}
              style={{
                color: "#fff"
              }}
            />
          ) : (
            startIcon
          )
        }
        endIcon={endIcon}
        onClick={onClick}
        disableRipple={true}
        disabled={disabled}
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        sx={style}
        type={type}
        form={form}
        onMouseDown={onMouseDown}
      >
        <div className='klayo-button_label' title={label as string}>
          {label}
        </div>
      </MuiButton>
    );
  }

  render() {
    const { tooltip } = this.props;

    return tooltip ? <Tooltip title={tooltip}>{this.renderButton()}</Tooltip> : this.renderButton();
  }
}
